/**
 * ImageLightbox — full-screen image viewer with keyboard & click navigation.
 * Usage:
 *   const [lb, setLb] = useState(null); // null or { images: [{src?, path?, alt, caption?}], idx }
 *   <img onClick={() => setLb({ images, idx: 0 })} />
 *   {lb && <ImageLightbox images={lb.images} initialIndex={lb.idx} onClose={() => setLb(null)} />}
 *
 * SEC-002 (2026-07-06): images[].path is preferred — the current image
 * lazily mints a 60 s dtoken via `useDownloadUrl`. Legacy `src` still
 * honoured for any not-yet-migrated caller.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDownloadUrl } from '../lib/dtoken';

export function ImageLightbox({ images, initialIndex = 0, onClose }) {
  const [idx, setIdx] = useState(initialIndex);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const current = images[idx] || images[0];
  // Lazy dtoken resolution — only for the currently displayed image.
  const { url: dtokenUrl } = useDownloadUrl(current && !current.src ? current.path : null);
  if (!current) return null;
  const displaySrc = current.src || dtokenUrl || '';

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/92 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors"
        data-testid="lightbox-close"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs text-[#a0a0a0] bg-[#1a1a1a]/80 px-3 py-1 rounded-full" data-testid="lightbox-counter">
          {idx + 1} / {images.length}
        </div>
      )}

      {/* Image */}
      <div
        className="relative max-w-[90vw] max-h-[88vh] flex items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={displaySrc}
          alt={current.alt || ''}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
        {current.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent rounded-b-lg px-4 py-3">
            <p className="text-sm text-white">{current.caption}</p>
          </div>
        )}
      </div>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            onClick={e => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#1a1a1a]/80 border border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#1a1a1a]/80 border border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          {/* Dot indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.slice(0, 12).map((_, i) => (
              <button
                key={i}
                onClick={e => { e.stopPropagation(); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-white' : 'w-1.5 bg-white/40'}`}
              />
            ))}
            {images.length > 12 && <span className="text-white/40 text-xs ml-1">+{images.length - 12}</span>}
          </div>
        </>
      )}
    </div>
  );
}
